import type { Messages } from "./ru";

type Loose<T> = { [K in keyof T]: T[K] extends string ? string : Loose<T[K]> };

const en: Loose<Messages> = {
  app: {
    name: "Datascratch",
    untitled: "Untitled",
    loading: "Loading…",
    slowTitle: "Can't open Datascratch",
    slowText: "Opening the saved session is taking too long. Try again or sign in again — your notes are safe.",
    retry: "Retry",
    signInAgain: "Sign in again",
    restoreFailed: "Couldn't open the saved session in this browser. Please sign in again — your notes are safe.",
    defaultSpace: "Personal",
    defaultNotebook: "Notes",
    welcomeTitle: "Welcome",
    welcomeBody1: "This is your first document. Write, add tasks with [ ] and draw right over the text.",
    welcomeBody2: "Press ⌘K to find anything or run a command.",
  },
  common: {
    expand: "Expand",
    collapse: "Collapse",
    cancel: "Cancel",
    save: "Save",
    create: "Create",
    delete: "Delete",
    rename: "Rename",
    close: "Close",
    done: "Done",
    copy: "Copy",
    copied: "Copied",
    continue: "Continue",
    back: "Back",
    open: "Open",
    retry: "Retry",
    search: "Search",
    more: "More",
    never: "Never",
    today: "Today",
    tomorrow: "Tomorrow",
    yesterday: "Yesterday",
    nextWeek: "Next week",
    pickDate: "Pick date…",
    none: "None",
    clear: "Clear",
    requiresOnline: "Requires a connection to the server",
  },
  auth: {
    revokedTitle: "The session on this device has ended",
    revokedText: "The server no longer accepts this sign-in. Please sign in again.",
    wipedTitle: "This device was disconnected from the account from another device",
    wipedText: "Its data has been deleted. Sign in again — your notes will load from the server.",
    revokedUnsent_one: "{{count}} unsent change is kept here and will be sent after you sign in.",
    revokedUnsent_few: "{{count}} unsent changes are kept here and will be sent after you sign in.",
    revokedUnsent_many: "{{count}} unsent changes are kept here and will be sent after you sign in.",
    revokedUnsent_other: "{{count}} unsent changes are kept here and will be sent after you sign in.",
    revokedForget: "Delete data from this device",
    keptTitle: "Unsent changes are kept",
    keptText: "Sign in to the same account to send them.",
    revokedForgetConfirm: "Delete all of this account's data from this device{{unsent}}? Nothing changes on the server or other devices.",
    revokedForgetUnsent: ", unsent changes included",
    tagline: "Notes where handwriting lives in the text.",
    signIn: "Sign in",
    signUp: "Create account",
    email: "Email",
    password: "Password",
    passwordRepeat: "Repeat password",
    passwordHint: "At least 10 characters. Your password encrypts your notes — the server never sees it.",
    passwordTooShort: "Password is shorter than 10 characters",
    passwordMismatch: "Passwords don't match",
    noAccount: "No account yet?",
    haveAccount: "Already have an account?",
    forgot: "Forgot password?",
    wrongCredentials: "Wrong email or password",
    emailTaken: "This email is already registered",
    serverUnavailable: "Server unavailable. Check your connection.",
    unsafeKdf: "The server sent unsafe password encryption parameters. Sign-in was stopped — check the server address.",
    deriving: "Securing your keys…",
    recoveryTitle: "Your recovery key",
    recoveryText:
      "Your notes are encrypted with a password only you know. If you forget it, this key is the only way back in. Keep it somewhere safe — we show it only once.",
    recoveryCopy: "Copy key",
    recoveryDownload: "Download as file",
    recoveryConfirmLabel: "Type the last 4 characters of the key to confirm",
    recoveryConfirmWrong: "Characters don't match",
    recoverySaved: "I saved my key",
    recoverySkip: "Skip",
    recoverySkipTitle: "Skip the key check?",
    recoverySkipWarning: "Without the recovery key a forgotten password can't be reset — your notes stay encrypted for good. You can see the key again in Settings.",
    recoveryBack: "Back to the key",
    recoverTitle: "Recover access",
    recoverText: "Enter your email and recovery key, then choose a new password. Your notes stay intact.",
    recoverOthersWiped: "Every device you are signed in on will be disconnected and its data deleted. Your notes stay on the server.",
    recoveryKey: "Recovery key",
    recoveryKeyInvalid: "The key looks wrong: 52 characters after DS1-",
    newPassword: "New password",
    recover: "Recover",
    wrongRecovery: "Email or recovery key doesn't match",
  },
  nav: {
    tabs: "Tabs",
    allDocs: "All documents",
    tasks: "Tasks",
    calendar: "Calendar",
    starred: "Starred",
    folders: "Notebooks",
    tags: "Tags",
    moreCount: "{{count}} more",
    allTags: "All tags",
    searchTags: "Find a tag",
    trash: "Trash",
    settings: "Settings",
    search: "Search",
    newDoc: "New document",
    newDocIn: "New document goes to “{{name}}”. Pick another notebook",
    newDocInShort: "in “{{name}}”",
    newFolder: "New folder",
    newNotebook: "New notebook",
    newSpace: "New space",
    spaces: "Spaces",
    addToNotebook: "Add to notebook",
    fullScreen: "Full screen",
    exitFullScreen: "Exit full screen",
    collapseSidebar: "Hide sidebar",
    expandSidebar: "Show sidebar",
    today: "Today",
    shared: "Shared with me",
  },
  sync: {
    online: "Synced",
    syncing: "Syncing…",
    connecting: "Connecting…",
    offline: "Offline — changes are saved on this device",
    pending_one: "{{count}} change waiting to sync",
    pending_few: "{{count}} changes waiting to sync",
    pending_many: "{{count}} changes waiting to sync",
    pending_other: "{{count}} changes waiting to sync",
    notSent: "Not sent: {{count}}",
    storageFull: "Browser storage is full — changes are not saved on this device.",
    storageFullHint: "They still reach the server while this tab is open; free up browser storage to keep them offline too.",
    unreadableTitle: "Some changes can't be read here yet",
    unreadableText: "They were saved with another key. The rest of the notebook is up to date.",
    unreadableShow: "Unreadable changes",
    unreadable_one: "{{count}} document has old changes that can't be read",
    unreadable_few: "{{count}} documents have old changes that can't be read",
    unreadable_many: "{{count}} documents have old changes that can't be read",
    unreadable_other: "{{count}} documents have old changes that can't be read",
    notSentHint: "The server did not accept these changes. They are saved on this device.",
    retry: "Retry",
  },
  tabs: {
    pin: "Pin tab",
    unpin: "Unpin tab",
    pinned: "pinned",
    tidy: "Tidy up tabs not opened for",
    tidyHint: "They go to «Recently closed» in the tab overview. Pinned and open tabs stay.",
    tidy1: "1 day",
    tidy7: "7 days",
    tidy30: "30 days",
    tidyNever: "Never",
    overview: "All tabs",
    more_one: "{{count}} more tab",
    more_few: "{{count}} more tabs",
    more_many: "{{count}} more tabs",
    more_other: "{{count}} more tabs",
    search: "Find a tab",
    groupNone: "All",
    groupNotebook: "By notebook",
    closeOthers: "Close others",
    recentlyClosed: "Recently closed",
    none: "No open tabs",
    count_one: "{{count}} tab",
    count_few: "{{count}} tabs",
    count_many: "{{count}} tabs",
    count_other: "{{count}} tabs",
  },
  docs: {
    insertPicture: "Insert picture",
    insertPictureFailed: "Could not insert the picture: {{reason}}",
    filterPlaceholder: "Search this list",
    filterClear: "Clear search",
    noMatches: "Nothing found",
    noMatchesHint: "Try other words, or search the whole space with ⌘K.",
    emptyTitle: "Nothing here yet",
    emptyText: "Create your first document — write, draw, add tasks.",
    emptyFolder: "This folder is empty",
    emptyShort: "Empty",
    emptyStarred: "Star what matters — it will show up here.",
    emptyTag: "No documents with this tag",
    emptyTrash: "Trash is empty",
    trashHint: "Documents are deleted forever after 30 days.",
    grid: "Grid",
    list: "List",
    sortUpdated: "Last modified",
    sortCreated: "Date created",
    sortTitle: "Title",
    updated: "Edited {{when}}",
    count_one: "{{count}} document",
    count_few: "{{count}} documents",
    count_many: "{{count}} documents",
    count_other: "{{count}} documents",
    subfolders: "Folders",
    folders_one: "{{count}} folder",
    folders_few: "{{count}} folders",
    folders_many: "{{count}} folders",
    folders_other: "{{count}} folders",
    star: "Add to starred",
    unstar: "Remove from starred",
    duplicate: "Duplicate",
    moveTo: "Move to…",
    closeTab: "Close tab",
    moveToNotebook: "Move to notebook…",
    moveToSpace: "Move to space…",
    movedTo: "Moved to “{{name}}”",
    moveFailed: "Couldn’t move the document. Try again.",
    moveNeedsNetwork: "Moving to another notebook needs a connection.",
    moveToAgentTitle: "Move to “{{name}}”?",
    moveToAgentWarning: "This notebook is open to AI agents: the server will be able to read this document so agents can work with it.",
    moveConfirm: "Move",
    moveToRoot: "Notebook root",
    toTrash: "Move to trash",
    restore: "Restore",
    deleteForever: "Delete forever",
    deleteForeverConfirm: "The document will be deleted on all devices. This can't be undone.",
    openInTab: "Open in tab",
    share: "Share…",
    history: "Version history",
    style: "Style",
    cover: "Cover",
    backlinks: "Linked from",
    noBacklinks: "No backlinks yet",
    trashed: "This document is in the trash",
    readOnlyNewer: "This document was made by a newer app version — read only. Please update.",
    notFound: "Document not found",
    notFoundText: "It may have been deleted or hasn't synced yet.",
    placeholder: "Write, or press / for blocks…",
    titlePlaceholder: "Untitled",
    moved: "Moved",
    trashedToast: "Moved to trash",
    undo: "Undo",
    inbox: "Inbox",
    daily: "Daily note",
    newInFolder: "Document in folder",
    created: "Created {{when}}",
  },
  folder: {
    newName: "New folder",
    color: "Color",
    icon: "Icon",
    addSub: "Subfolder",
    deleteConfirm: "The folder will be deleted; its documents and subfolders move one level up.",
  },
  notebook: {
    modeNotChangedOffline: "The server didn't answer — the mode is unchanged. Please try again.",
    modeNotChanged: "The server didn't accept the mode change: {{reason}}",
    name: "Name",
    newName: "New notebook",
    mode: "Mode",
    private: "Private",
    agent: "For agents",
    privateHint: "End-to-end encrypted. The key stays on your devices. Not available to agents.",
    agentHint: "The notebook key is kept on the server so AI agents with a token can read and edit notes.",
    agentWarning:
      "Honestly: in this mode the server can technically read this notebook. Decryption only happens for agent requests with a valid token, but privacy is lower than in a private notebook.",
    switchToAgent: "Open to agents",
    switchToPrivate: "Make private",
    switchToPrivateText: "The server deletes its copy of the key. Agents lose access to this notebook.",
    deleteConfirm: "The notebook and all its documents will be deleted on all devices.",
    dailyHere: "Daily notes and Inbox",
  },
  space: { newName: "New space", deleteNotEmpty: "Delete or move this space's notebooks first." },
  spaces: {
    newShared: "New shared space",
    newSharedName: "Shared space",
    invite: "Invite to space…",
    members: "Members…",
    shared: "Shared",
    sharedHint: "Shared space: all its members see it",
    makeSharedTitle: "Make “{{name}}” shared?",
    makeSharedText: "Members will see every notebook of this space — with all its history. Tabs, favourites and search stay each person's own.",
    makeShared: "Make shared",
    membersTitle: "Members of “{{name}}”",
    membersHint: "Everything in a shared space — notebooks, folders and notes — is seen by all members. Editors create and change, viewers only read.",
    role: { owner: "Owner", editor: "Can edit", viewer: "Read only" },
    leave: "Leave space",
    leaveTitle: "Leave “{{name}}”?",
    leaveText: "Its notebooks will disappear from this device. You can come back only with a new invitation from the owner.",
    left: "You left the shared space",
    gone: "Access to a shared space was closed",
    waiting: "Waiting for the owner's key — opens once the owner is online",
    moveToShared: "Move to a shared space",
    moveInTitle: "Move “{{notebook}}” to “{{space}}”?",
    moveInText: "Members of the space will see the whole notebook — its entire history, not just what comes next. This can't be undone: what they saw stays with them.",
    moveIn: "Move",
    movedIn: "The notebook is now in “{{space}}”",
    moveToPersonal: "Move to a personal space",
    moveOutTitle: "Move “{{notebook}}” to “{{space}}”?",
    moveOutText: "The notebook's key changes: members stop seeing anything new. What they already saw stays with them.",
    moveOut: "Move out",
    movedOut: "The notebook is now in “{{space}}”",
    deleteTitle: "Delete the shared space “{{name}}”?",
    deleteText: "The space with all its notebooks and notes is deleted for every member. This can't be undone.",
    readOnly: "Read only — you are a viewer in this space",
    failed: "That didn't work — please try again",
    conflict: "The server refused: the notebook has its own members or the data is outdated. Reload the page and try again.",
  },
  palette: {
    placeholder: "Find a document or command…",
    docs: "Documents",
    commands: "Commands",
    noResults: "No results",
    newDoc: "New document",
    newDocTitled: "Create “{{title}}”",
    goTasks: "Open tasks",
    goCalendar: "Open calendar",
    goToday: "Today's daily note",
    goSettings: "Settings",
    toggleTheme: "Toggle theme",
    toggleSidebar: "Toggle sidebar",
    newTask: "New task in Inbox",
    switchSpace: "Space: {{name}}",
  },
  tasks: {
    title: "Tasks",
    inbox: "Inbox",
    today: "Today",
    upcoming: "Upcoming",
    all: "All",
    logbook: "Completed",
    addPlaceholder: "New task…",
    emptyInbox: "Inbox is empty. Capture anything that doesn't belong to a document.",
    emptyToday: "Nothing for today. Have a good one!",
    emptyUpcoming: "No scheduled tasks.",
    emptyAll: "No tasks yet. Add a checkbox in any document — it shows up here.",
    overdue: "Overdue",
    noDate: "No date",
    schedule: "Schedule",
    deadline: "Deadline",
    complete: "Complete",
    reopen: "Reopen",
    cancel: "Cancel task",
    openDoc: "Open document",
    move: "Move to…",
    deleteTask: "Delete task",
    due: "due {{date}}",
    fromDoc: "from “{{title}}”",
    showDone: "Show completed",
    later: "Later",
  },
  calendar: {
    title: "Calendar",
    todayBtn: "Today",
    dailyNote: "Daily note",
    createDaily: "Create daily note",
    tasksForDay: "Tasks for the day",
    noTasks: "No tasks for this day",
    week: "Week",
    month: "Month",
  },
  shared: {
    title: "Shared with me",
    from: "Owner: {{email}}",
    waiting: "Waiting for the owner's key",
    waitingHint: "Opens once the owner is online",
    readOnly: "Read only",
    readOnlyHint: "The owner gave you read-only access",
    agents: "Open to AI agents",
    agentsHint: "The owner opened this notebook to AI agents",
    leave: "Leave",
    leaveTitle: "Leave “{{name}}”?",
    leaveText: "The documents will disappear from this device. You can come back only with a new invitation from the owner.",
    left: "You left the shared item",
    gone: "The owner closed your access",
  },
  openInApp: {
    invited: "You've been invited to a shared notebook or document.",
    orSignInHere: "or sign in here",
    button: "Open in the app",
    missing: "The app didn't open — it may not be installed. You can carry on here in the browser.",
  },
  invitations: {
    row: "Invitations",
    title: "Invitations",
    toNotebook: "invites you to a notebook",
    toSpace: "invites you to a shared space",
    toNote: "invites you to a document",
    until: "until {{date}}",
    accept: "Accept",
    decline: "Decline",
    accepted: "Done — see «Shared with me»",
    acceptedWaiting: "Accepted. It appears in «Shared with me» as soon as the owner is online",
    none: "No new invitations",
    gone: "This invitation was already accepted, declined, withdrawn or has expired",
    hint: "The name shows after you accept: the server does not know it, it is encrypted.",
  },
  share: {
    people: {
      title: "Invite by email",
      email: "Email",
      emailPlaceholder: "name@example.com",
      editor: "Can edit",
      viewer: "Can view",
      invite: "Invite",
      invited: "Invitation sent",
      hint: "They will see the invitation in the app — after signing up if they have no account yet. You can also send them the invitation link.",
      ownEmail: "That is your email.",
      you: "You",
      owner: "Owner",
      awaitingKey: "Accepted — access opens as soon as you are online",
      registeredAfter: "Signed up after the invitation — waiting for your confirmation",
      invitedUntil: "Invited · {{role}} · until {{date}}",
      copyInvite: "Copy invitation link",
      cancelInvite: "Withdraw invitation",
      remove: "Remove access",
      removeTitle: "Remove access for {{email}}?",
      removeText: "The key changes and they will not see new changes. What they have already seen stays with them — that is how end-to-end encryption works.",
      removed: "Access removed",
      limit: "A notebook can have at most 50 people, invitations included.",
      tooMany: "Too many invitations today. Try again tomorrow.",
      confirmLateTitle: "{{email}} signed up after the invitation — give access?",
      confirmLateText: "Sign-up does not confirm the email address. Give access only if you are sure it is the right person.",
      confirmLateYes: "Give access",
      confirmLateNo: "Not now",
      confirmNotInvitedTitle: "{{email}} is waiting for access, but this account never invited them — give access?",
      confirmNotInvitedText: "Your devices have no record of this invitation. If you did not invite this person, say no: the server may be asking for your notebook's key.",
      confirmKeyChangedTitle: "The key of {{email}} has changed — give access?",
      confirmKeyChangedText: "This person's account key is not the one your devices gave access to before. Say yes only if they told you they set up their account again.",
      giveAccess: "Give access",
    },
    title: "Share",
    note: "Document",
    notebook: "Notebook",
    guestLink: "Link for guests without an account",
    permission: "Access",
    view: "View only",
    edit: "Can edit and draw",
    expires: "Expires",
    expiresNever: "Until revoked",
    expires1d: "1 day",
    expires7d: "7 days",
    expires30d: "30 days",
    createLink: "Create link",
    copyLink: "Copy link",
    revoke: "Revoke",
    revoked: "Link revoked",
    active: "Active links",
    keyHint: "The encryption key is the part of the link after #. It never reaches the server: without the full link nobody can read the document.",
    agentModeNote: "The server can read this notebook (“for agents” mode).",
    expiresAt: "until {{date}}",
    noLinks: "No links yet",
    linksRecreated_one: "The access key changed — the guest link was re-created. Send the new one",
    linksRecreated_few: "The access key changed — {{count}} guest links were re-created. Send the new ones",
    linksRecreated_many: "The access key changed — {{count}} guest links were re-created. Send the new ones",
    linksRecreated_other: "The access key changed — {{count}} guest links were re-created. Send the new ones",
    linksShow: "Show",
  },
  guest: {
    loading: "Opening document…",
    badLink: "This link doesn't work",
    badLinkText: "It was revoked, has expired, or wasn't copied completely.",
    viewOnly: "View only",
    canEdit: "You can edit and draw",
    guest: "Guest",
    getApp: "Get Datascratch",
    documents: "Notebook documents",
  },
  history: {
    title: "Version history",
    empty: "No saved versions yet. Versions appear as you work on the document.",
    current: "Current",
    restore: "Restore this version",
    restored: "Version restored",
    loadFailed: "Couldn't load this version",
    preview: "Preview",
    version: "Version",
    changes: "Changes",
    noTextChanges: "The text did not change — drawings, style or attachments did.",
    you: "You",
    someone: "A member",
    guest: "Guest with a link",
    agent: "Agent",
    agentNamed: "Agent “{{name}}”",
  },
  style: {
    font: "Font",
    serif: "Serif",
    sans: "Sans",
    mono: "Mono",
    width: "Width",
    normal: "Normal",
    wide: "Wide",
    full: "Full width",
    paper: "Paper",
    plain: "Plain",
    dots: "Dots",
    grid: "Grid",
    lines: "Lines",
    cover: "Cover",
    addCover: "Add cover",
    removeCover: "Remove cover",
    icon: "Icon",
    addIcon: "Add icon",
  },
  pen: {
    showPalette: "Pen palette ({{tool}}) — expand",
    hidePalette: "Collapse the pen palette",
    tool: { text: "text", pen: "pen", pencil: "pencil", marker: "marker", highlighter: "highlighter", eraser: "eraser", lasso: "lasso" },
  },
  cover: {
    description: "Your own image: a photo, a screenshot or a picture file.",
    hint: "Drop an image here or paste it (⌘V)",
    choose: "Choose file",
    notImage: "That's not an image",
  },
  settings: {
    recoveryTitle: "Recovery key",
    recoveryUnconfirmed: "You haven't confirmed that you saved the key yet. Without it a forgotten password can't be recovered.",
    recoveryConfirmed: "Key saved. You can view it any time.",
    recoveryElsewhere: "The key was shown on the device you signed up on.",
    recoveryElsewhereUnconfirmed: "The key was shown on the device you signed up on. If it isn't saved, create a new one.",
    recoveryNew: "Create a new key",
    recoveryNewTitle: "New recovery key",
    recoveryNewText: "The old key stops working at once. The new one is shown once — save it.",
    recoveryNewCreate: "Create",
    securityLog: "Security log",
    securityEvent: {
      password_changed: "Password changed",
      recovery_used: "Signed in with the recovery key",
      recovery_key_rotated: "New recovery key",
    },
    recoveryShow: "Show key",
    recoveryDone: "Key confirmed",
    title: "Settings",
    account: "Account",
    notebooks: "Notebooks",
    agents: "Agents",
    appearance: "Appearance",
    devices: "Devices",
    security: "Security",
    email: "Email",
    signOut: "Sign out",
    signOutText: "Local data will be removed from this device.",
    signOutUnsent_one: "There is {{count}} unsent change on this device. It will be sent after you sign in to this account again.",
    signOutUnsent_few: "There are {{count}} unsent changes on this device. They will be sent after you sign in to this account again.",
    signOutUnsent_many: "There are {{count}} unsent changes on this device. They will be sent after you sign in to this account again.",
    signOutUnsent_other: "There are {{count}} unsent changes on this device. They will be sent after you sign in to this account again.",
    sendFirst: "Send first",
    changePassword: "Change password",
    currentPassword: "Current password",
    passwordChanged: "Password changed",
    wrongPassword: "Wrong password",
    deleteAccount: "Delete account",
    deleteAccountText: "All notes will be deleted from the server and all devices. This can't be undone.",
    thisDevice: "This device",
    lastSeen: "Last active {{when}}",
    revokeDevice: "End session",
    revokeDeviceTitle: "End the session on “{{name}}”?",
    revokeDeviceText: "All data on that device will be deleted, including changes not sent yet.",
    passwordOthersWiped: "Other devices will be disconnected and their data deleted. You can sign in on them with the new password.",
    theme: "Theme",
    themeSystem: "System",
    themeLight: "Light",
    themeDark: "Dark",
    language: "Language",
    mcpTitle: "Connect an AI agent",
    mcpText: "Add this address as an MCP server in Claude, ChatGPT or another agent. It opens a sign-in page where you choose notebooks (only “for agents” ones).",
    oauthConnection: "Connection",
    lastUsed: "Used {{when}}",
    agentTokens: "Agent tokens",
    agentTokensText: "A token gives an AI agent (Claude, ChatGPT…) access to chosen “for agents” notebooks via MCP or REST.",
    newToken: "New token",
    tokenName: "Name",
    tokenNotebooks: "Notebooks",
    tokenPermission: "Permission",
    tokenRead: "Read only",
    tokenReadWrite: "Read and write",
    allAgentNotebooks: "All notebooks for agents",
    tokenCreated: "Copy the token now — it won't be shown again.",
    tokenRevoke: "Revoke",
    noTokens: "No tokens",
    noAgentNotebooks: "No notebooks in “for agents” mode. Enable it for a notebook in the Notebooks tab.",
    audit: "Activity",
    noAudit: "No activity yet",
    storage: "Data on this device",
    storageText: "All notes are stored on this device and work offline.",
    dailyNotebook: "Notebook for daily notes",
    version: "Version {{version}}",
  },
  dates: { at: "{{date}} at {{time}}" },
  connect: {
    title: "Connecting your AI agent…",
    text: "The consent screen opens next — there you choose which notebooks to share.",
    badLink: "This connection link is invalid",
    badLinkText: "Start the connection again from your agent app (Claude, ChatGPT…).",
    failed: "Couldn't connect the agent",
  },
  pwa: { update: "A new version is available", reload: "Reload", offlineReady: "Ready to work offline" },
};

export default en;
